import { type UserProfile } from "@fitness-app/data-models";
import { getErrorMessage } from "@fitness-app/utils";

import { type AppThunk } from "../../../index";
import {
  getUserDetailsFailed,
  getUserDetailsStarted,
  getUserDetailsSuccess,
  updateUserDetailsSuccess,
} from "../reducer";
import { setUserPresence } from "./setUserPresence";
import { openRealtimeChannel } from "../../../helpers/openRealtimeChannel";
import { claimRealtimeSlot, ownsRealtimeSlot } from "../../../helpers/realtimeSlot";

export const subscribeToUserProfile =
  (id: string, isMobile = false): AppThunk =>
  async (dispatch, getState, { db, subscriptions, analytics, auth }) => {
    const claimedSlot = claimRealtimeSlot(subscriptions, "userProfile");

    dispatch(getUserDetailsStarted());
    try {
      const { data, error } = await db.from("user_profile").select().eq("id", id).single();

      if (error) {
        dispatch(getUserDetailsFailed(error.message));
        return;
      }
      if (data) {
        analytics.identify(id, { email: data.email, name: `${data.firstName} ${data.lastName}`, role: data.role });
        analytics.updateUserProperties(id, { email: data.email });
        dispatch(getUserDetailsSuccess(data as UserProfile));
      }

      // a newer subscribe or an unsubscribe took the slot while we were fetching
      if (!ownsRealtimeSlot(subscriptions, "userProfile", claimedSlot)) {
        return;
      }

      const channelName = `user-profile-${id}`;
      const channel = await openRealtimeChannel(db, channelName, subscriptions.realtimeUserDetailsChannel);

      // detaching the previous channel is another await - the slot may have changed hands
      if (!ownsRealtimeSlot(subscriptions, "userProfile", claimedSlot)) {
        await db.removeChannel(channel);
        return;
      }

      if (!isMobile) {
        dispatch(setUserPresence());
      }

      subscriptions.userDetailsChannelName = channelName;
      subscriptions.realtimeUserDetailsChannel = channel;

      subscriptions.realtimeUserDetailsChannel
        .on<UserProfile>(
          "postgres_changes",
          { event: "*", schema: "public", table: "user_profile", filter: `id=eq.${id}` },
          (payload) => {
            if (payload.eventType === "UPDATE") {
              // refresh to gain new claims
              void auth.refreshSession();
              dispatch(updateUserDetailsSuccess(payload.new));
              if (payload.new.email !== payload.old.email) {
                analytics.updateUserProperties(id, { email: payload.new.email });
              }
            }
          },
        )
        .subscribe();
    } catch (e) {
      dispatch(getUserDetailsFailed(getErrorMessage(e)));
    }
  };
